import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import './PresGen.css';
import api from '../../utils/axios';
import Loading from '../../components/Loading'; // Import the Loading component

function PresGen() {
    const [phase, setPhase] = useState(2); // Set the default phase to 2 (chatbot)
    const [presentationLength, setPresentationLength] = useState(5);
    const [userInputs, setUserInputs] = useState({
        foundersRelationship: '',
        technicalExperience: '',
        companyName: '',
        companyDescription: '',
        productDescription: '',
        progress: '',
        ideaOrigin: '',
        competitors: '',
        revenueModel: '',
        marketPotential: '',
        teamSpecialty: '',
        fundingNeeds: '',
    });
    const [script, setScript] = useState('');
    const [presentationContent, setPresentationContent] = useState(null);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [presentationToDelete, setPresentationToDelete] = useState(null);
    const [presentations, setPresentations] = useState([]);
    const [presentationId, setPresentationId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [isTyping, setIsTyping] = useState(false);
    const chatContainerRef = useRef(null);
    const initialMessageAdded = useRef(false); // Add this line
    const [userInputValue, setUserInputValue] = useState(''); // Add this line
    const [showInput, setShowInput] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [editingText, setEditingText] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Define questions before using them
    const questions = [
        {
            id: 'presentationLength',
            text: "Welcome to SwavAI’s Intelligent Pitch Generator! Drawing on proven strategies from leading incubators and accelerators such as Y Combinator and the McGill Dobson Centre for Entrepreneurship, I’m here to help you craft a powerful, investor-ready presentation. Let’s begin: how many minutes would you like your pitch to run?",
            type: 'number',
            min: 2,
        },
        {
            id: 'companyName',
            text: "Great! What's your company's name?",
            type: 'text',
            maxLength: 100, // Reasonable limit for company name
        },
        {
            id: 'companyDescription',
            text: "In 50 characters or less, describe what your company does:",
            type: 'text',
            maxLength: 50,
        },
        // Update remaining questions to use textarea type for unlimited input
        {
            id: 'productDescription',
            text: "What is your company going to make? Please describe your product and what it does or will do.",
            type: 'textarea',
        },
        {
            id: 'progress',
            text: "How far along are you?",
            type: 'textarea',
        },
        {
            id: 'ideaOrigin',
            text: "Why did you pick this idea to work on? Do you have domain expertise in this area? How do you know people need what you're making?",
            type: 'textarea',
        },
        {
            id: 'competitors',
            text: "Who are your competitors? What do you understand about your business that they don't?",
            type: 'textarea',
        },
        {
            id: 'revenueModel',
            text: "How do or will you make money? What is your revenue model or expected revenue model?",
            type: 'textarea',
        },
        {
            id: 'marketPotential',
            text: "What is the market potential for your product?",
            type: 'textarea',
        },
        {
            id: 'teamSpecialty',
            text: "What makes your team special? How long have the founders known one another and how did you meet?",
            type: 'textarea',
        },
        {
            id: 'fundingNeeds',
            text: "How much funding do you need and what will it be used for?",
            type: 'textarea',
        },
    ];

    // Add progress tracking
    const totalQuestions = questions.length;
    const progress = Math.round((currentQuestion / totalQuestions) * 100);

    // Add state for previous presentations modal
    const [showPreviousPresentations, setShowPreviousPresentations] = useState(false);

    useEffect(() => {
        if (!initialMessageAdded.current) { // Modify this block
            // Start with the welcome message
            addMessage(questions[0].text);
            initialMessageAdded.current = true;
        }
    }, []);

    const addMessage = (content, isBot = true) => {
        setMessages(prev => [...prev, {
            id: Date.now(),
            content,
            isBot,
        }]);
    };

    const handleKeyPress = async (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (userInputValue) {
                setShowInput(false);
                await handleAnswer(userInputValue);
            }
        }
    };

    const simulateTyping = async () => {
        setIsTyping(true);
        setShowInput(false);
        await new Promise(resolve => setTimeout(resolve, 1500));
        setIsTyping(false);
        setShowInput(true);
    };

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages]);

    const handleAnswer = async (answer) => {
        if (!answer) return; // Prevent adding empty messages

        const currentQuestionObj = questions[currentQuestion];
        addMessage(answer, false);

        // Save the answer to userInputs based on question ID
        setUserInputs(prev => ({
            ...prev,
            [currentQuestionObj.id]: answer,
        }));

        if (currentQuestion < questions.length - 1) {
            await simulateTyping();
            setCurrentQuestion(prev => prev + 1);
            addMessage(questions[currentQuestion + 1].text);
            setUserInputValue(''); // Clear input for next question
        } else {
            addMessage("Thank you, I have everything I need. I'll generate your presentation script now.");
            setShowInput(false);
            // Proceed to generate the script without inline loading animation
            handleGenerateScript();
        }
    };

    // Fetch saved presentations (Phase 5)
    useEffect(() => {
        const fetchPresentations = async () => {
            try {
                const response = await api.get('/presgen/presentations');
                setPresentations(response.data.presentations);
            } catch (error) {
                console.error('Error fetching presentations:', error);
                // ...handle error appropriately...
            }
        };
        fetchPresentations();
    }, []);

    const handleNextPhase = () => {
        setPhase(phase + 1);
    };

    const handlePreviousPhase = () => {
        setPhase(phase - 1);
    };

    const handleGenerateScript = async () => {
        try {
            // Save presentation data
            const response = await api.post('/presgen/save-presentation', { // Ensure the endpoint starts with '/presgen'
                length: presentationLength,
                type: 'Startup Pitch',
                userInputs,
            });
            const newPresentationId = response.data.presentationId;
            setPresentationId(newPresentationId);

            // Generate script
            const scriptResponse = await api.post('/presgen/generate-script', { // Ensure the endpoint starts with '/presgen'
                presentationId: newPresentationId,
                userInputs,
            });

            setScript(scriptResponse.data.scriptContent);
            handleNextPhase();
        } catch (error) {
            console.error('Error generating script:', error);
            // ...existing error handling...
        }
        setShowInput(true);
    };

    const handleSaveScript = async () => {
        try {
            // Save the script
            await api.post('/presgen/save-script', {
                presentationId: presentationId,
                scriptContent: script,
            });
            handleNextPhase();
            setPhase(1); // Reset to phase 1 after saving
        } catch (error) {
            console.error('Error saving script:', error);
            // ...existing error handling...
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setUserInputs({
            ...userInputs,
            [name]: value,
        });
    };

    const getPhaseTitle = (phaseNumber) => {
        const titles = {
            1: "Your Presentations",
            2: "PresGen",
            3: "Review Generated Script",
        };
        return titles[phaseNumber];
    };

    const handleEdit = (script) => {
        setIsEditing(true);
        setEditingText(script);
    };

    const handleSaveEdit = async () => {
        try {
            await api.post('/presgen/save-script', {
                presentationId: presentationId,
                scriptContent: editingText,
            });
            setScript(editingText);
            setIsEditing(false);
        } catch (error) {
            console.error('Error saving edit:', error);
        }
    };

    // Modify the Create New Presentation button handler
    const handleCreateNewPresentation = () => {
        // Reset necessary states
        setUserInputs({
            foundersRelationship: '',
            technicalExperience: '',
            companyName: '',
            companyDescription: '',
            productDescription: '',
            progress: '',
            ideaOrigin: '',
            competitors: '',
            revenueModel: '',
            marketPotential: '',
            teamSpecialty: '',
            fundingNeeds: '',
        });
        setPresentationId(null);
        setMessages([]);
        setCurrentQuestion(0);
        setUserInputValue('');
        initialMessageAdded.current = false; // Corrected line
        setPhase(2); // Move to Phase 2 to start the questionnaire
        addMessage(questions[0].text); // Add the first bot message
    };

    // Update the loadPresentation function
    const loadPresentation = async (presentationId) => {
        try {
            const response = await api.get(`/presgen/presentation/${presentationId}`);
            const { userInputs: savedInputs, script: savedScript, presentationContent: savedContent } = response.data;

            setUserInputs(savedInputs || {});
            setScript(savedScript || '');
            setPresentationContent(savedContent || '');
            setPresentationId(presentationId);

            setPhase(3); // Navigate to the script editing phase
            setIsEditing(false);
        } catch (error) {
            console.error('Error loading presentation:', error);
            // Handle error appropriately
        }
    };

    // Add deletePresentation function
    const deletePresentation = async (presentationId) => {
        try {
            await api.delete(`/presgen/presentation/${presentationId}`);
            setPresentations(prev => prev.filter(p => p.id !== presentationId));
            setShowDeleteConfirmation(false);
            setPresentationToDelete(null);
        } catch (error) {
            console.error('Error deleting presentation:', error);
            // Handle error appropriately
        }
    };

    return (
        <div className="presgen">
            {/* Add Previous Presentations button */}
            <button
                className="previous-presentations-button"
                onClick={() => setShowPreviousPresentations(true)}
            >
                Previous Presentations
            </button>

            {/* Add Previous Presentations Modal */}
            {showPreviousPresentations && (
                <div className="modal">
                    <div className="modal-content">
                        <h3>Previous Presentations</h3>
                        <div className="presentations-list">
                            {presentations.map(presentation => (
                                <div
                                    key={presentation.id}
                                    className="presentation-item"
                                    onClick={() => loadPresentation(presentation.id)}
                                >
                                    <h4>{presentation.company_name}</h4>
                                    <p>Created: {new Date(presentation.created_at).toLocaleDateString()}</p>
                                </div>
                            ))}
                        </div>
                        <button onClick={() => setShowPreviousPresentations(false)}>Close</button>
                    </div>
                </div>
            )}

            <div className="phase-indicator">
                {[1, 2, 3].map(p => (
                    <div key={p} className={`phase-dot ${phase === p ? 'active' : ''}`} />
                ))}
            </div>

            {phase === 1 && (
                <>
                    <h2 className="phase-title">{getPhaseTitle(phase)}</h2>
                    <div className="saved-presentations">
                        <button
                            className="create-new-button"
                            onClick={handleCreateNewPresentation}
                        >
                            Create New Presentation
                        </button>
                        <div className="presentations-grid">
                            {presentations.map(presentation => (
                                <div
                                    key={presentation.id}
                                    className="presentation-card"
                                >
                                    <div className="presentation-card__header">
                                        <h4 className="presentation-card__title">
                                            {presentation.company_name || 'Untitled Presentation'}
                                        </h4>
                                        <span className={`presentation-card__status ${presentation.status === 'complete'
                                            ? 'presentation-card__status--complete'
                                            : 'presentation-card__status--draft'
                                            }`}>
                                            {presentation.status === 'complete' ? 'Complete' : 'Draft'}
                                        </span>
                                    </div>

                                    <div className="presentation-card__meta">
                                        <span>Created: {new Date(presentation.created_at).toLocaleDateString()}</span>
                                        {presentation.length && (
                                            <span>•</span>
                                        )}
                                        {presentation.length && (
                                            <span>{presentation.length} minutes</span>
                                        )}
                                        {presentation.type && (
                                            <span>•</span>
                                        )}
                                        {presentation.type && (
                                            <span>{presentation.type}</span>
                                        )}
                                    </div>

                                    {presentation.script && (
                                        <div className="presentation-card__preview">
                                            {presentation.script.substring(0, 200)}...
                                        </div>
                                    )}

                                    <div className="presentation-card__actions">
                                        <button
                                            className="presentation-card__button presentation-card__button--delete"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setPresentationToDelete(presentation.id);
                                                setShowDeleteConfirmation(true);
                                            }}
                                        >
                                            Delete
                                        </button>
                                        <button
                                            className="presentation-card__button presentation-card__button--edit"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                loadPresentation(presentation.id);
                                            }}
                                        >
                                            Continue Editing
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    {showDeleteConfirmation && (
                        <div className="modal-overlay" onClick={() => setShowDeleteConfirmation(false)}>
                            <div className="confirm-modal" onClick={(e) => e.stopPropagation()}> {/* Prevent event propagation */}
                                <h3>Confirm Delete</h3>
                                <p>Are you sure you want to delete this presentation?</p>
                                <div className="modal-actions">
                                    <button onClick={() => setShowDeleteConfirmation(false)}>Cancel</button>
                                    <button onClick={() => deletePresentation(presentationToDelete)}>Delete</button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {phase === 2 && (
                <>
                    <h2 className="phase-title">{getPhaseTitle(phase)}</h2>
                    <div className="chat-container" ref={chatContainerRef}>
                        {messages.map(message => (
                            <div key={message.id}
                                className={`message ${message.isBot ? 'bot-message' : 'user-message'}`}>
                                {message.content}
                            </div>
                        ))}
                        {isTyping && (
                            <div className="typing-indicator">
                                <div className="typing-dot"></div>
                                <div className="typing-dot" style={{ animationDelay: '0.2s' }}></div>
                                <div className="typing-dot" style={{ animationDelay: '0.4s' }}></div>
                            </div>
                        )}
                        {/* Show input container only when showInput is true */}
                        {showInput && !isTyping && (
                            <div className="input-container">
                                {questions[currentQuestion].type === 'number' && (
                                    <input
                                        type="number"
                                        min={questions[currentQuestion].min || 1}
                                        value={userInputValue}
                                        onChange={(e) => setUserInputValue(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        placeholder="Enter value"
                                        autoFocus
                                    />
                                )}
                                {questions[currentQuestion].type === 'text' && (
                                    <input
                                        type="text"
                                        maxLength={questions[currentQuestion].maxLength}
                                        value={userInputValue}
                                        onChange={(e) => setUserInputValue(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        placeholder="Enter text"
                                        autoFocus
                                    />
                                )}
                                {questions[currentQuestion].type === 'textarea' && (
                                    <textarea
                                        value={userInputValue}
                                        onChange={(e) => setUserInputValue(e.target.value)}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                handleKeyPress(e);
                                            }
                                        }}
                                        placeholder="Enter your response..."
                                        autoFocus
                                        rows={3}
                                    />
                                )}
                                <button
                                    className="nav-button next"
                                    onClick={() => handleAnswer(userInputValue)}
                                    disabled={!userInputValue}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </div>
                </>
            )}

            {phase === 3 && (
                <>
                    <h2 className="phase-title">{getPhaseTitle(phase)}</h2>
                    <div className="script-container">
                        {isEditing ? (
                            <div className="editing-container">
                                <textarea
                                    className="script-editor editable"
                                    value={editingText}
                                    onChange={(e) => setEditingText(e.target.value)}
                                    rows={10}
                                />
                                <div className="editing-actions">
                                    <button onClick={() => setIsEditing(false)}>Cancel</button>
                                    <button onClick={handleSaveEdit}>Save Changes</button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="script-actions">
                                    <button onClick={() => handleEdit(script)}>Edit Script</button>
                                </div>
                                <ReactMarkdown className="script-editor">
                                    {script}
                                </ReactMarkdown>
                            </>
                        )}
                    </div>
                </>
            )}

            {/* ...existing phase content... */}

            <div className="phase-navigation">
                {phase > 1 && (
                    <button
                        className="nav-button previous"
                        onClick={phase === 3 ? () => setPhase(1) : handlePreviousPhase} // Update handler for phase 3
                    >
                        {phase === 2 ? 'My Presentations' : (phase === 3 ? 'My Presentations' : 'Previous')}
                    </button>
                )}
                {phase === 2 && (
                    <button
                        className="nav-button next"
                        onClick={handleGenerateScript}
                        disabled={currentQuestion < questions.length}
                    >
                        Generate Script
                    </button>
                )}
                {phase === 3 && (
                    <button
                        className="nav-button next"
                        onClick={handleSaveScript}
                        disabled={!script}
                    >
                        Save & Continue
                    </button>
                )}
                {/* ...additional navigation buttons for other phases if needed... */}
            </div>
            {isLoading && <Loading />} {/* Use the Loading component if needed */}
        </div>
    );
}

export default PresGen;
